import { AbstractControl, ValidationErrors } from '@angular/forms';

export function phoneFormatValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  if (!value) return null;

  const digitsOnly = value.replace(/\D/g, '');
  const valid = /^\d{7,15}$/.test(digitsOnly);

  return valid ? null : { invalidPhone: true };
}

/** Country code the platform treats as local. */
export const DEFAULT_COUNTRY_PREFIX = '55';

/**
 * Digits only, optionally country-prefixed. Does NOT validate.
 *
 * The contact importer's rule, which lived inline in
 * `contact-upload-csv.component.ts` (`applyMappingAndBuildRows`). Lifted here so
 * the campaign file audience can apply the same rule the importer applies, and
 * so it sits next to `phoneFormatValidator` instead of in a parallel utility.
 *
 * Mirrors `ContactUtil.sanitizePhone` in `@app/trendmulti-core`: the browser
 * decides what to show as valid, the server revalidates the same way, and the
 * two must not disagree or the audience the user confirmed is not the one
 * dispatched.
 *
 * `phoneFormatValidator` above is a different job — reactive-form validation,
 * 7 to 15 digits, no country logic. It is not a substitute for this.
 */
export function sanitizePhone(value: unknown, applyCountryPrefix = false, countryPrefix = DEFAULT_COUNTRY_PREFIX): string {
  const digits = String(value ?? '').replace(/\D+/g, '');
  if (!digits) return '';

  if (applyCountryPrefix && !digits.startsWith(countryPrefix)) {
    return `${countryPrefix}${digits}`;
  }
  return digits;
}

/**
 * Whether a sanitized number can be dispatched to: a Brazilian number must be
 * 12 digits (`55` + DDD + 8) or 13 (`55` + DDD + 9). Anything else is treated
 * as international and not length-checked.
 */
export function isValidPhone(phone: string): boolean {
  if (!phone) return false;
  if (phone.startsWith(DEFAULT_COUNTRY_PREFIX)) {
    return phone.length === 12 || phone.length === 13;
  }
  return true;
}

/**
 * True for a Brazilian number — the only ones RCS reaches.
 *
 * Mirrors `ContactUtil.isBrazilianPhone` in `@app/trendmulti-core`, the same rule
 * the audience summary applies when it counts and the staging applies when it
 * materialises the dispatch. `isValidPhone` above does NOT imply this: a number
 * written without the country code passes as international and is still out of
 * reach on RCS.
 */
export function isBrazilianPhone(phone: string): boolean {
  return !!phone && phone.startsWith(DEFAULT_COUNTRY_PREFIX);
}
